import * as React from 'react'

import { DailyRestEventModel } from 'app/models/dailyRestEventModel'
import { BaseEventModel } from 'app/models/baseEventModel'
import { formatTimespan } from 'app/common/dateHelper'

import { useScene } from '../context/SceneContext'
import { useRestLanes } from '../context/LanesContext'
import { useConfig } from '../context/ConfigContext'
import { BOTTOM_PADDING } from '../utils/layout'
import { RestInfoLabel } from './RestInfoLabel'

export type DailyRestBlockProps = {
  dailyRest: DailyRestEventModel
  index: number
}

const color = '#D58CFF'

// Below this the "11:00 / 11:00" label would not fit on the bar.
const MIN_LABEL_WIDTH = 90

// "11:00 / 11:00", or "09:00 (1x9) / 09:00" for the n-th reduced rest of the week -
// on screen this is the tooltip, on a printout it has to be drawn on the bar.
const formatRestInfo = (dailyRest: DailyRestEventModel): string =>
  dailyRest.isReducedRest
    ? `${formatTimespan(dailyRest.restDuration)} (${dailyRest.dailyReducedRestNumber}x9) / ${formatTimespan(dailyRest.formalRestDuration)}`
    : `${formatTimespan(dailyRest.restDuration)} / ${formatTimespan(dailyRest.formalRestDuration)}`

const prepareTooltipInfo = (dailyRest: DailyRestEventModel): BaseEventModel => {
  const restInfo = formatRestInfo(dailyRest)
  const event: BaseEventModel = {
    startTime: dailyRest.startTime,
    endTime: dailyRest.realRestEndTime,
    typeName: dailyRest.isReducedRest
      ? 'eventName.dailyReducedRest'
      : 'eventName.dailyRest',
    color: color,
    info: restInfo,
  }
  return event
}

export const DailyRestBlock: React.FC<DailyRestBlockProps> = React.memo(
  (props: DailyRestBlockProps) => {
    const scene = useScene()
    const { restLaneFor, restIsTop } = useRestLanes()
    const { printMode } = useConfig()

    const dailyRest = props.dailyRest

    const width = scene.widthOf(dailyRest.startTime, dailyRest.realRestEndTime)
    const position = scene.timeToX(dailyRest.startTime)

    const lane = restLaneFor(dailyRest.startTime, dailyRest.realRestEndTime)
    const isTop = restIsTop(dailyRest.startTime, dailyRest.realRestEndTime)

    return (
      <div
        className="tw-flex tw-flex-col tw-items-start tw-justify-end"
        style={{
          position: 'absolute',
          left: position,
          top: 0,
          bottom: 0,
          paddingBottom: BOTTOM_PADDING,
        }}
      >
        <div
          style={{
            background: color,
            borderColor: '#000000',
            width,
            height: 6,
            marginBottom: lane * 6,
            borderTopLeftRadius: isTop ? 3 : 0,
            borderTopRightRadius: isTop ? 3 : 0,
            paddingTop: 1,
            pointerEvents: 'auto',
            position: 'relative',
          }}
          data-tip={JSON.stringify(prepareTooltipInfo(dailyRest))}
          data-for="eventTooltip"
        >
          {printMode && width > MIN_LABEL_WIDTH && (
            <span
              style={{
                position: 'absolute',
                left: 4,
                bottom: 6,
                fontSize: 10,
                lineHeight: '10px',
                fontWeight: 500,
                letterSpacing: 0.5,
                whiteSpace: 'nowrap',
                color: '#3A3F48',
              }}
            >
              {formatRestInfo(dailyRest)}
            </span>
          )}

          {dailyRest.isReducedRest && (
            <RestInfoLabel
              color={color}
              duration={formatRestInfo(dailyRest)}
              left={position}
              width={width}
              lane={lane}
            />
          )}
        </div>
      </div>
    )
  },
)

DailyRestBlock.displayName = 'DailyRestBlock'
